import type { ButtonHTMLAttributes } from "react";

import SendCircleIcon from "@expert/common-ui/assets/SendCircleIcon";
import { ActionIcon, type ActionIconProps, Stack, Textarea } from "@mantine/core";

import type { SpellCheckResponse, SpellCheckStatus as SpellCheckStatusType } from "@/sdk/agent/chat/types";

import classes from "./message-input-styles.module.css";
import { SpellCheckStatus } from "./spellcheck/SpellCheckStatus";

const MESSAGE_INPUT_PLACEHOLDER = "Message customer";

function SendButton({ onClick, disabled, loading }: ButtonHTMLAttributes<HTMLButtonElement> & ActionIconProps) {
    return (
        <ActionIcon
            className={classes.sendButton}
            data-testid="send-chat-message-button"
            id="send-chat-message-button"
            disabled={!!disabled}
            loading={loading}
            loaderProps={{ size: "sm", color: "gray.7" }}
            onClick={onClick}
            type="submit"
            variant="transparent"
            radius={38}
            size={38}
        >
            <SendCircleIcon disabled={!!disabled} />
        </ActionIcon>
    );
}

export interface MessageInputSectionSpellCheckProps {
    status: SpellCheckStatusType;
    result?: SpellCheckResponse;
    errorMessage?: string;
    successDisplayDurationMs?: number;
    errorDisplayDurationMs?: number;
    allowSendOriginal?: boolean;
    onCancel: () => void;
    onSendWithChanges: () => void;
    onSendOriginal?: () => void;
    onDismiss: () => void;
}

export interface MessageInputSectionProps {
    value: string;
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
    onBlur: () => void;
    onSubmit: () => void;
    isSendDisabled: boolean;
    isInputDisabled: boolean;
    isSending: boolean;
    spellCheckStatus: MessageInputSectionSpellCheckProps;
    /** When true, applies fs-mask class to hide input from FullStory */
    containsPotentialPii?: boolean;
    textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
}

/**
 * Presentational wrapper component: spellcheck status section + message textarea + send button.
 * All behavior is passed via props from MessageInput.
 */
export function MessageInputSection({
    value,
    onChange,
    onKeyDown,
    onBlur,
    onSubmit,
    isSendDisabled,
    isInputDisabled,
    isSending,
    spellCheckStatus,
    containsPotentialPii = false,
    textareaRef,
}: MessageInputSectionProps) {
    return (
        <Stack gap={0} w="100%">
            <SpellCheckStatus
                status={spellCheckStatus.status}
                result={spellCheckStatus.result}
                errorMessage={spellCheckStatus.errorMessage}
                successDisplayDurationMs={spellCheckStatus.successDisplayDurationMs}
                errorDisplayDurationMs={spellCheckStatus.errorDisplayDurationMs}
                allowSendOriginal={spellCheckStatus.allowSendOriginal}
                onCancel={spellCheckStatus.onCancel}
                onSendWithChanges={spellCheckStatus.onSendWithChanges}
                onSendOriginal={spellCheckStatus.onSendOriginal}
                onDismiss={spellCheckStatus.onDismiss}
            />

            <Textarea
                ref={textareaRef}
                id="chat-timeline-message-input"
                data-testid="chat-timeline-message-input"
                placeholder={MESSAGE_INPUT_PLACEHOLDER}
                disabled={isInputDisabled}
                value={value}
                onChange={onChange}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                rightSection={<SendButton disabled={isSendDisabled} loading={isSending} onClick={onSubmit} />}
                className={containsPotentialPii ? "fs-mask" : undefined}
                classNames={{ input: classes.input }}
                radius={0}
                size="xl"
                autosize
                minRows={1}
                maxRows={8}
                autoFocus
            />
        </Stack>
    );
}
